import type { TemplateRef } from '@angular/core';
import { Component, DestroyRef, HostBinding, inject, input, output, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { DynamicLocaleService } from '../dynamic-locale.service';
import { IconLoaderService } from '../icon-loader.service';
import { InterpolatedBlueprintService } from '../interpolated-blueprint.service';
import {
  BlueprintBase,
  CoverBlueprint,
  FullTemplateInfo,
  GroupBase,
  InterpolatedBlueprint,
  Layout,
} from '../models/blueprint.model';
import {
  DataObject,
  RecordUpdateEmission,
  ReportInfo,
  ReportOptions,
  ReportView,
  TemplateProjectionMap,
} from '../models/report.model';
import {
  PropertyGroup,
  ReportAllRecordsImage,
  TemplateField,
  TemplateFieldsCatalog,
  ViewTemplateCatalog,
} from '../models/template.model';
import { ReportBluePrintInterpolationService } from '../report-blueprint-interpolation.service';
import { ReportExportViewService } from '../report-export-view.service';
import { ReportImagesService } from '../report-images.service';
import { ReportViews, TEMPLATE_ASSOCIATIONS } from '../shared/constants/template.const';
import {
  SelectTemplateDialogComponent,
  SelectTemplateDialogInputData,
} from '../shared/select-template-dialog/select-template-dialog.component';
import { NotificationService } from '../shared/services/notification.service';
import { TemplateDynamicService } from '../template-dynamic.service';
import { ReportResetImagesService } from '../views/presentation-dynamic-view/record-body/record-image/report-reset-images.service';
import { LocaleId } from '../models/locale.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush, template: '' })
export class AbstractDynamicReportViewComponent implements OnInit, OnDestroy {
  @HostBinding('attr.loaded') loadedStatus = 'loading';

  readonly reportData = input<ReportInfo>();
  readonly currentView = input<ReportView>();
  readonly reportLocale = input<number>();
  readonly columns = input<{ [entity: string]: { [columnField: string]: any } }>();
  /** @deprecated Use {@link templateProjectionMap} instead. */
  readonly confidentialityTmpl = input<TemplateRef<any>>();
  /** Map of TemplateRef to be used on the view. */
  readonly templateProjectionMap = input<TemplateProjectionMap>();
  readonly reportOptions = input<ReportOptions>();
  readonly isPDF = input<boolean>(false);
  readonly customConfigs = input<{ symbols: DataObject }>();
  readonly isViewHidden = input<boolean>();
  readonly isUKFeaturesEnabled = input<boolean>();
  readonly reportUpdateClick = output<RecordUpdateEmission>();
  readonly templateIdChange = output<ReportView>();

  templateFieldsCatalog: ViewTemplateCatalog;
  headerGroups: GroupBase<TemplateField>[];
  bodyGroups: GroupBase<TemplateField>[];
  footerGroups: GroupBase<TemplateField>[];
  contactGroups: GroupBase<TemplateField>[];
  imageGroups: GroupBase<TemplateField>[];

  propertyGroups: PropertyGroup[];

  private readonly _templateId: Subject<number> = new Subject<number>();
  private templatePopupOpen = false;
  private reportIsFromChinaIndiaOrHK = false;
  private lastInitKey: string | null = null;
  set templateId(value: number) {
    this._templateId.next(value);
  }
  get templateId$() {
    return this._templateId.asObservable();
  }

  protected reportExportViewService = inject(ReportExportViewService);
  protected interpolatedBlueprintService = inject(InterpolatedBlueprintService);
  private readonly iconLoaderService = inject(IconLoaderService);
  protected destroyRef = inject(DestroyRef);
  private readonly notificationService = inject(NotificationService);
  private readonly reportImagesService = inject(ReportImagesService);
  private readonly localeService = inject(DynamicLocaleService);
  private readonly templateDynamicService = inject(TemplateDynamicService);
  private readonly reportResetImagesService = inject(ReportResetImagesService);
  private readonly reportBluePrintInterpolationService = inject(ReportBluePrintInterpolationService);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    effect(() => {
      const columns = this.columns();
      const currentView = this.currentView();
      const reportLocale = this.reportLocale();
      const reportData = this.reportData();
      if (
        (!!columns || !!currentView || reportLocale != null) &&
        !!columns &&
        !!currentView &&
        reportLocale !== null
      ) {
        const key = `${reportData?.reportId}::${currentView.id}::${currentView.templateId}::${reportLocale}`;
        if (key === this.lastInitKey) return;
        this.lastInitKey = key;
        this.initializeDynamicReport();
        this.fetchAdditionalImagesOfReport();
        if (
          reportData &&
          reportData.originalViews &&
          reportData.originalViews.some(a => a.id === ReportViews.comparison) &&
          (currentView.id === ReportViews.comparison || currentView.id === ReportViews.presentation)
        ) {
          this.fetchPropertyGroupsOfReport(currentView.id);
        }
        this.localeService.setReportLocaleId(reportLocale);
      }
    });
  }

  ngOnInit() {
    this.reportIsFromChinaIndiaOrHK = [
      LocaleId.ChinaChinese,
      LocaleId.ChinaEnglish,
      LocaleId.IndiaEnglish,
      LocaleId.HongKongEnglish,
    ].includes(this.reportLocale());
    this.initInterpolationSubscription();
  }

  ngOnDestroy(): void {
    // required by angular-l10n
  }

  public onTemplateSelected(selectedTemplateId: number) {
    const currentView = this.currentView();
    const oldTemplateId = currentView.templateId;
    const updatedView = { ...currentView, templateId: selectedTemplateId };
    const { catalogUri, templateId } = updatedView;
    this.triggerBlueprintInterpolation(catalogUri, templateId);
    if (oldTemplateId !== selectedTemplateId) {
      this.templateIdChange.emit({ ...updatedView });
    }
  }

  public onTemplateSelectedLater(templateAndViewAndSelectedId) {
    const { selectedTemplateId, viewsAndTemplates } = templateAndViewAndSelectedId;
    const currentView = this.currentView();
    const oldTemplateId = currentView.templateId;
    if (oldTemplateId !== selectedTemplateId) {
      this.templateIdChange.emit({ ...viewsAndTemplates });
    }
  }

  private fetchTemplateAndViewToSend(selectedTemplateId: number) {
    const currentView = this.currentView();
    this.templateDynamicService.setTemplateId(currentView.id, selectedTemplateId);
    const viewsAndTemplates = this.templateDynamicService.setViewandTemplate(
      this.reportLocale(),
      currentView,
      selectedTemplateId
    );
    if (viewsAndTemplates.length) {
      const templateAndViewAndSelectedId = { selectedTemplateId, viewsAndTemplates };
      viewsAndTemplates.forEach((item, index) => {
        this.templateDynamicService.saveAssociatedTemplates(this.reportData(), item).subscribe(() => {
          this.onTemplateSelectedLater(templateAndViewAndSelectedId);
        });
      });
    }
  }

  protected prepareGroupsObjectToDisplay(
    interpolatedBlueprint: InterpolatedBlueprint,
    contactsInterpolatedBlueprint?: InterpolatedBlueprint
  ) {
    const groupsData = this.interpolatedBlueprintService.flattenBlueprintColumnsToGroups(interpolatedBlueprint);
    /*
     * Returns the groups which are only needed in HTML view. @{filterOutInHtml}
     * is boolean value, added in template blue print json
     */
    const groups = groupsData.filter(group => !group.filterOutInHtml);
    this.headerGroups = groups.filter(group => group.htmlHeader);
    this.bodyGroups = groups.filter(
      group => !group.htmlHeader && !group.htmlFooter && !group.contacts && !group.hasImages
    );
    this.footerGroups = groups.filter(group => group.htmlFooter);
    this.imageGroups = groups.filter(group => group.hasImages);
    if (contactsInterpolatedBlueprint) {
      const contactsGroupsData =
        this.interpolatedBlueprintService.flattenBlueprintColumnsToGroups(contactsInterpolatedBlueprint);
      this.contactGroups = contactsGroupsData.filter(group => !group.filterOutInHtml);
    }
  }

  private initInterpolationSubscription() {
    this.templateId$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(templateId => {
          const mainLayoutInter$ = this.interpolatedBlueprintService
            .fetchInterpolationByTemplateId$(templateId, Layout.MainLayout)
            .pipe(take(1));
          const contactsLayoutInter$ = this.interpolatedBlueprintService
            .fetchInterpolationByTemplateId$(templateId, Layout.Contacts)
            .pipe(take(1));
          return forkJoin([mainLayoutInter$, contactsLayoutInter$]);
        })
      )
      .subscribe(([interpolatedBlueprint, contactsInterpolatedBlueprint]) => {
        this.prepareGroupsObjectToDisplay(interpolatedBlueprint, contactsInterpolatedBlueprint);
        this.loadedStatus = 'true';
        this.cdr.markForCheck();
      });
  }

  private initializeDynamicReport() {
    const currentView = this.currentView();
    const reportData = this.reportData();
    const reportLocale = this.reportLocale();
    const columns = this.columns();
    const { catalogUri, id } = currentView;
    let { templateId } = currentView;
    // find if reportData views has templateId
    if (!templateId) {
      const selectedReportView = reportData.views.find(item => item.id === currentView.id);
      templateId = selectedReportView ? selectedReportView.templateId : null;
    }
    if (TEMPLATE_ASSOCIATIONS[reportLocale]) {
      const templateInfo = this.templateDynamicService.getTemplates(currentView);
      if (templateId) {
        this.triggerBlueprintInterpolation(catalogUri, templateId);
      }
      templateInfo.subscribe(templates => {
        this.templateFieldsCatalog = templates;
        this.cdr.markForCheck();
        if (TEMPLATE_ASSOCIATIONS[reportLocale][0] === id) {
          let observableResult$: Observable<number> = of(templateId);
          if (templateId == null) {
            observableResult$ = this.templateDynamicService.templateDialog(reportData.reportId, id);
            observableResult$.pipe(take(1)).subscribe(selectedTemplateId => {
              this.templateDynamicService.setTemplateId(id, selectedTemplateId);
              this.fetchTemplateAndViewToSend(selectedTemplateId);
              this.reportResetImagesService
                .setReportAdditionalImages(reportData.records, reportData.reportId, selectedTemplateId)
                .subscribe(() => {
                  this.fetchAdditionalImagesOfReport();
                });
            });
          } else {
            this.templateDynamicService.setTemplateId(id, templateId);
            this.onTemplateSelected(templateId);
          }
        }
      });
    } else {
      const initializationFlow: Observable<number> = this.triggerTemplateCatalogRetrieval(catalogUri, templateId);
      initializationFlow.pipe(take(1)).subscribe(selectedTemplateId => {
        this.templatePopupOpen = false;
        this.onTemplateSelected(selectedTemplateId);
      });
    }
  }

  private triggerTemplateCatalogRetrieval(catalogUri: string, templateId: number): Observable<number> {
    const reportData = this.reportData();
    const currentView = this.currentView();
    return this.reportExportViewService.getViewTemplateFieldsCatalog(catalogUri).pipe(
      switchMap(templateFieldsCatalog => {
        this.templateFieldsCatalog = templateFieldsCatalog;
        const dialogInputData: SelectTemplateDialogInputData = {
          reportId: reportData.reportId,
          templates: templateFieldsCatalog,
          viewId: currentView.id,
          autoSelectTemplate: this.reportIsFromChinaIndiaOrHK,
        };
        let observableResult$: Observable<number> = of(templateId);
        if (templateId == null) {
          // Auto-select without showing dialog if: view is hidden, only 1 template, or auto-select flag is set
          if (this.isViewHidden() || templateFieldsCatalog.length === 1 || this.reportIsFromChinaIndiaOrHK) {
            // Select and save the first template by default without showing dialog
            const selectedTemplate = templateFieldsCatalog[0];
            const saveTemplate$ = this.reportExportViewService.saveTemplate(
              reportData.reportId,
              currentView.id,
              selectedTemplate
            );
            observableResult$ = saveTemplate$.pipe(map(() => selectedTemplate.templateId));
          } else {
            if (this.templatePopupOpen) {
              return;
            }
            this.templatePopupOpen = true;
            observableResult$ = this.notificationService
              .openDialog<number>(SelectTemplateDialogComponent, dialogInputData)
              .pipe(filter(selectedTemplate => selectedTemplate != null));
          }
        }
        return observableResult$;
      })
    );
  }

  private triggerBlueprintInterpolation(catalogUri: string, templateId: number) {
    const reportData = this.reportData();
    const reportLocale = this.reportLocale();
    const columns = this.columns();
    this.reportBluePrintInterpolationService
      .blueprintInterpolation(catalogUri, templateId, reportData.entity, reportLocale, columns)
      .subscribe(templateIdInstance => (this.templateId = templateIdInstance));
  }

  /**
   * Get and map all the data needed for the report, including the cover,
   * the blueprint and template fields data.
   *
   * @param partialTemplateInfo
   */
  private getFullTemplateInfo(
    partialTemplateInfo: [BlueprintBase, TemplateFieldsCatalog]
  ): Observable<FullTemplateInfo> {
    const [blueprint, templateCatalog] = partialTemplateInfo;
    let coverSlideInfo$: Observable<CoverBlueprint> = of(null);
    // verify that cover path exists
    if (blueprint.coverPath != null) {
      coverSlideInfo$ = this.reportExportViewService.getCoverPageBluePrint(blueprint.coverPath);
    }
    return coverSlideInfo$.pipe(
      // map to FullTemplateInfo
      map(coverSlideInfo => {
        return { coverSlideInfo, blueprint, templateCatalog };
      })
    );
  }

  /**
   * this method is used to fetch the additional image id of the report.
   */
  private fetchAdditionalImagesOfReport() {
    const reportData = this.reportData();
    this.reportExportViewService.getImages(reportData.reportId).subscribe((res: ReportAllRecordsImage) => {
      this.reportImagesService.setReportRecordImages(res);
    });
  }

  /**
   * this method is used to fetch the property groups of the report.
   */
  private fetchPropertyGroupsOfReport(viewId: number) {
    const reportData = this.reportData();
    this.reportExportViewService
      .getPropertyGroups(reportData.reportId, viewId)
      .subscribe((res: PropertyGroup[]) => {
        this.interpolatedBlueprintService.setReportPropertyGroups(res);
      });
    this.interpolatedBlueprintService.getReportPropertyGroupsStream().subscribe(res => {
      this.propertyGroups = res;
      this.cdr.markForCheck();
    });
  }
}
